/* eslint-disable max-lines-per-function */
import type {
  BackgroundOpacity,
  CaptionTrack,
  FontFamily,
  FontSize,
  Level,
  SubtitlePosition,
  TextColor,
  TextEdgeStyle,
} from "@lumi/player-core";
import { Button } from "@lumi/ui-headless";
import { ArrowLeft } from "lucide-react";
import type { ReactElement } from "react";
import React, { useState } from "react";
import { OVERLAY_GRADIENT_BOTTOM, OVERLAY_GRADIENT_TOP } from "../../../shared/constants/player";
import { useAutoHideControls } from "../hooks/useAutoHideControls";
import { ControlsBar } from "./ControlsBar";
import { CustomSubtitleOverlay } from "./CustomSubtitleOverlay";
import { SettingsPanel } from "./SettingsPanel";
import { ShortcutsHUD } from "./ShortcutsHUD";

export type OverlayChromeProps = Readonly<{
  containerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isPlaying: boolean;
  onTogglePlay: () => void;
  time: number;
  duration: number;
  onSeek: (nextSeconds: number) => void;
  levels: ReadonlyArray<Level>;
  selectedLevel: number | "auto";
  onChangeLevel: (val: number | "auto") => void;
  captions: ReadonlyArray<CaptionTrack>;
  activeCaption: number | "off";
  onChangeCaption: (val: number | "off") => void;
  playbackRate: number;
  onChangePlaybackRate: (rate: number) => void;
  subtitlePosition: SubtitlePosition;
  onChangeSubtitlePosition: (position: SubtitlePosition) => void;
  subtitleFontSize: FontSize;
  onChangeSubtitleFontSize: (size: FontSize) => void;
  subtitleBackgroundOpacity: BackgroundOpacity;
  onChangeSubtitleBackgroundOpacity: (opacity: BackgroundOpacity) => void;
  subtitleTextColor: TextColor;
  onChangeSubtitleTextColor: (color: TextColor) => void;
  subtitleFontFamily: FontFamily;
  onChangeSubtitleFontFamily: (family: FontFamily) => void;
  subtitleTextEdge: TextEdgeStyle;
  onChangeSubtitleTextEdge: (edge: TextEdgeStyle) => void;
  onBack?: () => void;
  vttUrl?: string | null;
  onControlsVisibilityChange?: (visible: boolean) => void;
}>;

export const OverlayChrome = React.memo(function OverlayChrome(props: OverlayChromeProps): ReactElement {
  const {
    containerRef,
    videoRef,
    isPlaying,
    onTogglePlay,
    time,
    duration,
    onSeek,
    levels,
    selectedLevel,
    onChangeLevel,
    captions,
    activeCaption,
    onChangeCaption,
    playbackRate,
    onChangePlaybackRate,
    subtitlePosition,
    onChangeSubtitlePosition,
    subtitleFontSize,
    onChangeSubtitleFontSize,
    subtitleBackgroundOpacity,
    onChangeSubtitleBackgroundOpacity,
    subtitleTextColor,
    onChangeSubtitleTextColor,
    subtitleFontFamily,
    onChangeSubtitleFontFamily,
    subtitleTextEdge,
    onChangeSubtitleTextEdge,
    onBack,
    vttUrl,
    onControlsVisibilityChange,
  } = props;

  const [activePanel, setActivePanel] = useState<"quality" | "captions" | null>(null);

  const { isVisible, handleMouseMove, handleControlsMouseEnter, handleControlsMouseLeave, showControls, cursorStyle } =
    useAutoHideControls({
      videoRef,
      onControlsVisibilityChange,
    });

  // Toggle traffic lights visibility on Electron macOS
  React.useEffect(() => {
    // prettier-ignore
    if (typeof window !== "undefined" && window.electronAPI?.setTrafficLightsVisible) { // NOSONAR
      window.electronAPI.setTrafficLightsVisible(isVisible); // NOSONAR
    }
  }, [isVisible]);

  const handleTogglePlay = () => {
    showControls();
    onTogglePlay();
  };

  const handleSeek = (s: number) => {
    showControls();
    onSeek(s);
  };

  return (
    <div className="pointer-events-none absolute inset-0" role="application" aria-label="Player overlay">
      {/* Invisible full-screen layer to capture mouse movement anywhere */}
      <div // NOSONAR
        className="pointer-events-auto absolute inset-0 bg-transparent"
        style={{ cursor: cursorStyle }}
        onMouseMove={handleMouseMove}
      >
        {/* Draggable titlebar region for Electron (macOS only) - inside the mouse capture layer */}
        <div
          className="absolute top-0 left-0 right-0 h-[52px] electron-drag-region"
          style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
        />
      </div>

      {/* Custom subtitle overlay (replaces native TextTrack rendering) */}
      <CustomSubtitleOverlay
        videoRef={videoRef}
        currentTime={time}
        enabled={activeCaption !== "off"}
        activeCaption={activeCaption}
        controlsVisible={isVisible}
        subtitlePosition={subtitlePosition}
        fontSize={subtitleFontSize}
        backgroundOpacity={subtitleBackgroundOpacity}
        textColor={subtitleTextColor}
        fontFamily={subtitleFontFamily}
        textEdge={subtitleTextEdge}
      />

      {/* Top overlay controls */}
      <div
        className={`absolute inset-x-0 top-0 transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div // NOSONAR
          className={`pointer-events-auto ${OVERLAY_GRADIENT_TOP} px-3 pt-12 pb-10 flex justify-between`}
          onMouseEnter={handleControlsMouseEnter}
          onMouseLeave={handleControlsMouseLeave}
        >
          {/* Back Button */}
          {onBack && (
            <Button variant="ghost-sm" aria-label="Go back" onClick={onBack}>
              <ArrowLeft size={20} />
            </Button>
          )}

          {/* Spacer for centering */}
          <div className="flex-1" />

          {/* Keyboard Shortcuts HUD */}
          <ShortcutsHUD />
        </div>
      </div>

      {/* Bottom controls bar */}
      <div
        className={`absolute inset-x-0 bottom-0 transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div // NOSONAR
          className={`pointer-events-auto ${OVERLAY_GRADIENT_BOTTOM} px-3 pb-3 pt-10`}
          onMouseEnter={handleControlsMouseEnter}
          onMouseLeave={handleControlsMouseLeave}
        >
          <ControlsBar
            containerRef={containerRef}
            videoRef={videoRef}
            isPlaying={isPlaying}
            onTogglePlay={handleTogglePlay}
            time={time}
            duration={duration}
            onSeek={handleSeek}
            levels={levels}
            selectedLevel={selectedLevel}
            onChangeLevel={onChangeLevel}
            captions={captions}
            activeCaption={activeCaption}
            onChangeCaption={onChangeCaption}
            playbackRate={playbackRate}
            onChangePlaybackRate={onChangePlaybackRate}
            subtitlePosition={subtitlePosition}
            onChangeSubtitlePosition={onChangeSubtitlePosition}
            subtitleFontSize={subtitleFontSize}
            onChangeSubtitleFontSize={onChangeSubtitleFontSize}
            subtitleBackgroundOpacity={subtitleBackgroundOpacity}
            onChangeSubtitleBackgroundOpacity={onChangeSubtitleBackgroundOpacity}
            subtitleTextColor={subtitleTextColor}
            onChangeSubtitleTextColor={onChangeSubtitleTextColor}
            subtitleFontFamily={subtitleFontFamily}
            onChangeSubtitleFontFamily={onChangeSubtitleFontFamily}
            subtitleTextEdge={subtitleTextEdge}
            onChangeSubtitleTextEdge={onChangeSubtitleTextEdge}
            vttUrl={vttUrl}
            activePanel={activePanel}
            onActivePanelChange={setActivePanel}
          />
        </div>
      </div>

      {/* Settings Panel - rendered at OverlayChrome level to avoid affecting subtitle positioning */}
      {/* Positioned absolutely to OverlayChrome root, not relative to controls container */}
      {/* Only shown when controls are visible, but not affected by their transition */}
      {isVisible && activePanel && (
        <div // NOSONAR
          className="absolute inset-0 mb-12 z-0 pointer-events-none"
          onMouseEnter={handleControlsMouseEnter}
          onMouseLeave={handleControlsMouseLeave}
        >
          <SettingsPanel
            isOpen={true}
            onClose={() => setActivePanel(null)}
            mode={activePanel}
            levels={levels}
            selectedLevel={selectedLevel}
            onChangeLevel={onChangeLevel}
            captions={captions}
            activeCaption={activeCaption}
            onChangeCaption={onChangeCaption}
            playbackRate={playbackRate}
            onChangePlaybackRate={onChangePlaybackRate}
            subtitlePosition={subtitlePosition}
            onChangeSubtitlePosition={onChangeSubtitlePosition}
            subtitleFontSize={subtitleFontSize}
            onChangeSubtitleFontSize={onChangeSubtitleFontSize}
            subtitleBackgroundOpacity={subtitleBackgroundOpacity}
            onChangeSubtitleBackgroundOpacity={onChangeSubtitleBackgroundOpacity}
            subtitleTextColor={subtitleTextColor}
            onChangeSubtitleTextColor={onChangeSubtitleTextColor}
            subtitleFontFamily={subtitleFontFamily}
            onChangeSubtitleFontFamily={onChangeSubtitleFontFamily}
            subtitleTextEdge={subtitleTextEdge}
            onChangeSubtitleTextEdge={onChangeSubtitleTextEdge}
          />
        </div>
      )}
    </div>
  );
});
